import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Loader2, Users, BookOpen, Star, Heart, BarChart, Package, Award, 
  Database, Settings, Activity, TrendingUp, Calendar, AlertTriangle,
  RefreshCw, ListChecks, FileText, Search, Cpu, ShieldAlert, UserPlus,
  LayoutGrid, ArrowUpRight, ShoppingBag, Plus, Trash2, Pencil, MessageSquare
} from 'lucide-react';
import { 
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, AreaChart, Area 
} from 'recharts';
import StatCard from './StatCard';
import HealthCard from './HealthCard';
import { SystemStats, GameReview } from '../../types'; // Mantendo SystemStats conforme linter
import { useTheme } from '../../contexts/ThemeContext'; // Importar useTheme

interface DashboardContentProps {
  statsLoading: boolean;
  statistics: any | null; // Usando any temporariamente
  registrationsLoading: boolean;
  userRegistrations: any[];
  systemHealth: { status: string; message: string };
  checkSystemHealth: () => void;
  reviewsLoading: boolean;
  recentReviews: GameReview[];
  recentLoginsLoading: boolean;
  recentLogins: any[];
}

const DashboardContent: React.FC<DashboardContentProps> = ({ 
  statsLoading, statistics, registrationsLoading, userRegistrations, 
  systemHealth, checkSystemHealth, reviewsLoading, recentReviews,
  recentLoginsLoading, recentLogins
}) => {
  const { theme } = useTheme(); // Usar o hook useTheme
  
  // Função para processar os dados de registro de usuários (pode ser movida para um utilitário se usada em outro lugar)
  const processRegistrationData = (registrationData: any[]) => {
      // Mapa para contar registros por dia
      const countByDay: Record<string, number> = {};
      
      // Contar registros para cada dia
      registrationData.forEach(registration => {
        if (!registration.created_at) return;
        
        // Formatar a data como YYYY-MM-DD
        const date = new Date(registration.created_at);
        const day = date.toISOString().split('T')[0];
        
        // Incrementar contador para este dia
        countByDay[day] = (countByDay[day] || 0) + 1;
      });
      
      // Criar um array de datas ordenadas
      const sortedDays = Object.keys(countByDay).sort();
      
      // Limitar a últimos 30 dias se houver muitos dados
      const recentDays = sortedDays.length > 30 ? sortedDays.slice(-30) : sortedDays;
      
      // Preparar dados para o gráfico
      return recentDays.map(day => {
        const [year, month, dayNum] = day.split('-');
        const formattedDate = `${dayNum}/${month}`;
        
        return {
          date: formattedDate,
          fullDate: day,
          registrations: countByDay[day]
        };
      });
  };
    
  const processedUserRegistrations = processRegistrationData(userRegistrations);

  const chartLineColor = theme === 'dark' ? '#818CF8' : '#4F46E5'; // indigo-400 dark, indigo-600 light
  const chartFillColor = theme === 'dark' ? '#818CF8' : '#4F46E5';
  const chartGridColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const chartTextColor = theme === 'dark' ? '#9CA3AF' : '#6B7280'; // gray-400 dark, gray-500 light

  return (
    <div className={`space-y-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
      {/* Cards principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsLoading ? (
          <div className="col-span-4 flex justify-center items-center h-40">
            <Loader2 className={`w-8 h-8 animate-spin ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} />
          </div>
        ) : statistics ? (
          <>
            <StatCard icon={Users} value={statistics.total_users} label="Usuários" color="border-blue-500 dark:border-blue-400" />
            <StatCard icon={BookOpen} value={statistics.total_games} label="Jogos" color="border-indigo-500 dark:border-indigo-400" />
            <StatCard icon={Star} value={statistics.total_ratings} label="Avaliações" color="border-amber-500 dark:border-amber-400" />
            <StatCard icon={Package} value={statistics.total_collections} label="Em Coleções" color="border-green-500 dark:border-green-400" />
          </>
        ) : (
          <div className={`col-span-4 p-4 rounded-md text-center ${theme === 'dark' ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
            Falha ao carregar estatísticas
          </div>
        )}
      </div>
      
      {/* Gráfico de Registros de Usuários */}
      <div className={`rounded-lg shadow-md p-6 ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
        <h2 className={`text-lg font-semibold mb-4 flex items-center ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
          <UserPlus className={`h-5 w-5 mr-2 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} />
          Novos Usuários por Dia
        </h2>
        {registrationsLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className={`w-8 h-8 animate-spin ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} />
          </div>
        ) : processedUserRegistrations.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={processedUserRegistrations} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                <XAxis dataKey="date" stroke={chartTextColor} />
                <YAxis allowDecimals={false} stroke={chartTextColor} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: theme === 'dark' ? '#374151' : '#FFFFFF', // gray-700 dark, white light
                    borderColor: theme === 'dark' ? '#4B5563' : '#E5E7EB', // gray-600 dark, gray-200 light
                    color: theme === 'dark' ? '#F3F4F6' : '#1F2937' // gray-100 dark, gray-800 light
                  }}
                  itemStyle={{ color: theme === 'dark' ? '#A5B4FC' : '#4338CA' }} // indigo-300 dark, indigo-700 light
                  labelStyle={{ color: theme === 'dark' ? '#D1D5DB' : '#374151' }} // gray-300 dark, gray-700 light
                />
                <Area type="monotone" dataKey="registrations" stroke={chartLineColor} fill={chartFillColor} fillOpacity={0.3} activeDot={{ r: 6, fill: chartFillColor, stroke: chartLineColor }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className={`flex flex-col items-center justify-center h-64 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} text-center`}>Sem dados de registro disponíveis</p>
          </div>
        )}
      </div>
      
      {/* Status do sistema e Avaliações Recentes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <HealthCard systemHealth={systemHealth} checkSystemHealth={checkSystemHealth} />
        
        <div className={`rounded-lg shadow-md p-6 ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
          <h2 className={`text-lg font-semibold mb-4 flex items-center ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
            <MessageSquare className={`h-5 w-5 mr-2 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} />
            Avaliações Recentes
          </h2>
          {reviewsLoading ? (
            <div className="flex justify-center items-center h-40"><Loader2 className={`w-6 h-6 animate-spin ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} /></div>
          ) : recentReviews.length > 0 ? (
            <ul className="space-y-3 max-h-80 overflow-y-auto pr-2 styled-scrollbar-dark dark:styled-scrollbar-dark">
              {recentReviews.map((review) => (
                <li key={review.id} className={`border-b pb-3 last:border-b-0 last:pb-0 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2 flex-grow min-w-0">
                      {review.avatar_url ? (
                        <img src={review.avatar_url} alt={review.username || 'Usuário'} className="w-6 h-6 rounded-full flex-shrink-0"/>
                      ) : (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                          {(review.username || 'U').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className={`font-medium text-sm truncate ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>{review.username || 'Usuário'}</span>
                    </div>
                    <div className="flex items-center flex-shrink-0 ml-2">
                      <span className={`font-bold text-sm ${theme === 'dark' ? 'text-amber-400' : 'text-amber-500'}`}>{review.rating}/5</span>
                    </div>
                  </div>
                  <div className="mt-1 mb-2">
                    <Link to={`/games/${(review as any).game_slug || review.game_id}`} className={`text-sm font-medium line-clamp-1 ${theme === 'dark' ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:underline'}`}>
                      {(review as any).game_name || 'Jogo'}
                    </Link>
                  </div>
                  <p className={`text-sm line-clamp-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{(review as any).review_text}</p>
                  <div className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                    {new Date(review.created_at).toLocaleDateString()}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className={`text-center p-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Nenhuma avaliação recente encontrada</div>
          )}
          <div className="mt-3 text-right">
            <Link to="/admin/reviews" className={`text-sm inline-flex items-center ${theme === 'dark' ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:underline'}`}>
              Ver todas <ArrowUpRight className="ml-1 h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>
      
      {/* Estatísticas detalhadas e Atividades Recentes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {statistics && statistics.most_popular_games && statistics.most_popular_games.length > 0 && (
          <div className={`rounded-lg shadow-md p-6 ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
            <h2 className={`text-lg font-semibold mb-4 flex items-center ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
              <TrendingUp className={`h-5 w-5 mr-2 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} /> Jogos Mais Populares
            </h2>
            <ul className="space-y-3">
              {statistics.most_popular_games.map((game: { id: string; name: string; count: number; slug?: string }) => (
                <li key={game.id} className="flex justify-between items-center">
                  <Link to={`/games/${game.slug || game.id}`} className={`line-clamp-1 ${theme === 'dark' ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:underline'}`}>{game.name}</Link>
                  <span className={`px-2 py-1 rounded-full text-xs flex-shrink-0 ${theme === 'dark' ? 'bg-indigo-700 text-indigo-200' : 'bg-indigo-100 text-indigo-800'}`}>{game.count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {statistics && statistics.most_popular_categories && statistics.most_popular_categories.length > 0 && (
          <div className={`rounded-lg shadow-md p-6 ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
            <h2 className={`text-lg font-semibold mb-4 flex items-center ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
              <ListChecks className={`h-5 w-5 mr-2 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} /> Categorias Populares
            </h2>
            <div className="flex flex-wrap gap-2">
              {statistics.most_popular_categories.map((category: { category: string; count: number }) => (
                <div key={category.category} className={`px-3 py-1 rounded-full text-sm ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{category.category}</span>
                  <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} ml-1`}>({category.count})</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className={`rounded-lg shadow-md p-6 ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
          <h2 className={`text-lg font-semibold mb-4 flex items-center ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
            <Calendar className={`h-5 w-5 mr-2 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} /> Atividades Recentes
          </h2>
          {recentLoginsLoading ? (
            <div className="flex justify-center items-center h-32"><Loader2 className={`w-6 h-6 animate-spin ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} /></div>
          ) : recentLogins.length > 0 ? (
            <ul className="space-y-3">
              {recentLogins.slice(0, 5).map((login, index) => (
                <li key={index} className={`text-sm border-b last:border-b-0 pb-2 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex justify-between">
                    <span className={`font-medium truncate ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>{login.user_email || (login.user_id ? login.user_id.substring(0, 8) : 'ID Indisponível')}</span>
                    <span className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'} text-xs flex-shrink-0 ml-2`}>{new Date(login.created_at).toLocaleString()}</span>
                  </div>
                  <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{login.event_type}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'} text-sm`}>
              <p>Sem atividades recentes para exibir.</p>
              <p className={`mt-2 text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>A tabela de eventos de autenticação pode não estar configurada.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardContent; 